/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, Grid } from '@ui-propulsion/react-components';
import Link from 'next/link';
import parse from 'html-react-parser';
import Prequal from '../prequal/prequal';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import styles from './heroTemplate.module.scss';
import ExtensionCta from '../extensionCta/extensionCta';
import GradientMask from '../gradientMask/gradientMask';
import handleCTAClick from '../../services/helpers/handleCTAClick';
import useSetHeroImg from '../../hooks/useSetHeroImg';
import Breadcumbs from '../breadcrumbs/breadcrumbs';
import StickyButtton from '../stickyButton/stickyButton';
import Tag from '../tag/tag';
import HelpCenterSearch from '../helpCenterSearch/helpCenterSearch';
import { ctaTheme } from '../../utils/util';
import SmartImage from '../smartImage/smartImage';

const TABLET_MD_AND_UP = 768;

function HeroTemplate({
  title,
  headline,
  subHeroText,
  heroDesc,
  promotion,
  image,
  mobImage,
  backgroundSize,
  secondaryImage,
  mobSecondaryImage,
  theme,
  prequal,
  cta,
  stickyButton,
  infoCta,
  infoIcon,
  logos,
  socialProof,
  tiles,
  config,
  affectCreditScore,
  heroWhiteCopyBg,
  backgroundPosition,
  breadCrumbs,
  activePage,
  searchBox,
}) {
  const [heroHeight, setHeroHeight] = useState(0);
  const { width } = useWindowDimensions() || {};
  const propsGradientMask = config.gradientMask ?? null;
  const heroImg = useSetHeroImg(image, mobImage, width);
  const heroSecImage = useSetHeroImg(secondaryImage, mobSecondaryImage, width);
  const [ctaThemes, setCtaThemes] = useState(styles.redSolid);
  const heroRef = useRef();
  useEffect(() => {
    setHeroHeight(0);
    if (width > TABLET_MD_AND_UP) {
      setHeroHeight(heroRef.current.clientHeight);
    }
  }, [width]);

  useEffect(() => {
    setCtaThemes(ctaTheme(cta?.ctaTheme));
  }, [cta]);

  const sectionStyle = {
    backgroundImage: `url(${heroImg?.src})`,
    backgroundPosition: backgroundPosition || 'center',
  };
  const gridColumns = activePage === 'resourceCenter' ? 12 : 6;

  return (
    <section
      className={`${styles.heroTemplate} ${
        !!theme && styles[`heroTemplate${theme}`]
      } ${styles[`${activePage}`]}`}
    >
      <div
        id="heroTemplateBackground"
        className={`${styles.heroTemplateBackground} ${
          backgroundSize === 'contain' && styles.bgContain
        }`}
        aria-hidden="true"
        style={sectionStyle}
      />
      {propsGradientMask && <GradientMask {...propsGradientMask} />}
      <div className={styles.heroTemplateBackgroundGradient}>
        <div
          ref={heroRef}
          className={`${styles.heroTemplateContent} ${styles.heroTemplateContentHiddenHeader}`}
          style={{ minHeight: heroHeight }}
        >
          <Grid container>
            <Grid
              item
              xs={12}
              sm={gridColumns}
              lg={gridColumns}
              md={gridColumns}
              className={`${styles.heroContent} ${
                prequal?.prequalType === 'Home Card' ? styles.cardView : ''
              }`}
            >
              <div
                className={`${
                  heroWhiteCopyBg === 'Behind all content'
                    ? styles.behindAll
                    : ''
                } ${`${styles.heroTemplateHeaderLayout} ${
                  prequal?.prequalType === 'Home Card'
                    ? styles.heroTemplateHeaderLayoutPrequalCard
                    : ''
                }`}`}
              >
                {breadCrumbs && (
                  <div className={styles.breadCrumb}>
                    <Breadcumbs
                      links={breadCrumbs.link}
                      backLink={breadCrumbs.backLink}
                    />
                  </div>
                )}
                {promotion && (
                  <div className={styles.promotion}>
                    <Tag label={promotion} />
                  </div>
                )}
                {(title && title?.length > 0) ||
                (title && title?.string && title?.string?.length > 0) ? (
                  <>
                    <h1 className={styles.heroTemplateTitleText}>{title}</h1>
                    {tiles ? (
                      <div className={styles.headingProgress}>
                        <div className={styles.headingProgressContent}>
                          {(tiles === 'all' || tiles === 'before') && (
                            <span
                              className={styles.headingProgressTileBefore}
                            />
                          )}
                          {headline && (
                            <h2>
                              <span className={styles.headingProgressHighlight}>
                                {parse(headline)}
                              </span>
                              {(tiles === 'all' || tiles === 'after') && (
                                <span
                                  className={styles.headingProgressTileAfter}
                                />
                              )}
                            </h2>
                          )}
                        </div>
                      </div>
                    ) : (
                      headline && (
                        <h2 className={styles.heroTemplateHeroText}>
                          {parse(headline)}
                        </h2>
                      )
                    )}
                  </>
                ) : (
                  <div>
                    {tiles ? (
                      <div className={styles.headingProgress}>
                        <div className={styles.headingProgressContent}>
                          {(tiles === 'all' || tiles === 'before') && (
                            <span
                              className={styles.headingProgressTileBefore}
                            />
                          )}
                          {headline && (
                            <h1>
                              <span className={styles.headingProgressHighlight}>
                                {parse(headline)}
                              </span>
                              {(tiles === 'all' || tiles === 'after') && (
                                <span
                                  className={styles.headingProgressTileAfter}
                                />
                              )}
                            </h1>
                          )}
                        </div>
                      </div>
                    ) : (
                      headline && (
                        <h1 className={styles.heroTemplateHeroText}>
                          {parse(headline)}
                        </h1>
                      )
                    )}
                  </div>
                )}
                {((subHeroText && subHeroText?.length > 0) ||
                  (subHeroText &&
                    subHeroText?.string &&
                    subHeroText?.string?.length > 0)) && (
                  <h3 className={styles.heroTemplateHeroSubText}>
                    {parse(subHeroText)}
                  </h3>
                )}
                {((heroDesc && heroDesc?.length > 0) ||
                  (heroDesc &&
                    heroDesc?.string &&
                    heroDesc?.string?.length > 0)) && (
                  <div
                    className={`${styles.heroSubHeroText} ${
                      heroWhiteCopyBg === 'Only Behind Discription Text'
                        ? styles.heroWhiteCopyBgOnlyDescription
                        : ''
                    }`}
                  >
                    {parse(heroDesc)}
                  </div>
                )}
              </div>
              {prequal && (
                <div
                  className={`${styles.heroTemplatePrequalContent} ${
                    prequal?.prequalType === 'Home Card'
                      ? styles.heroTemplatePrequalCard
                      : ''
                  }`}
                >
                  <Prequal prequal={prequal} theme={theme} />
                </div>
              )}
              {cta &&
                (cta.type === 'wildfireCta' ? (
                  <div className={styles.extensionBtn}>
                    <ExtensionCta {...cta} />
                  </div>
                ) : (
                  <div className={styles.getStartedBtn}>
                    <Button
                      className={`${styles.benefitsCta} ${styles.investorCta} ${ctaThemes}`}
                      variant="tertiary"
                      onClick={(e) => handleCTAClick(e, cta)}
                      href={cta.link}
                      data-testid="heroCTA"
                    >
                      {cta.label}
                    </Button>
                  </div>
                ))}
              {stickyButton && <StickyButtton {...stickyButton} />}
              {infoCta && (
                <div className={styles.infoSection}>
                  <Link href={infoCta.link}>
                    <SmartImage
                      src={infoIcon.path}
                      alt={infoIcon.alt ?? infoCta.label}
                      width={infoIcon.width}
                      height={infoIcon.height}
                    />
                    <span className={styles.infoText}>{infoCta.label}</span>
                  </Link>
                </div>
              )}
              {affectCreditScore && (
                <div className={styles.noteText}>
                  <span>{parse(affectCreditScore)}</span>
                </div>
              )}
            </Grid>
            {heroSecImage && (
              <Grid item xs={12} sm={6} lg={6} md={6}>
                <div className={styles.heroSecondaryImage}>
                  <SmartImage
                    src={heroSecImage?.src}
                    alt={heroSecImage?.alt ?? 'Hero Section Image'}
                    priority
                    height={heroSecImage?.height}
                    width={heroSecImage?.width}
                  />
                </div>
              </Grid>
            )}
          </Grid>
          {logos?.length > 0 ? (
            <div className={styles.heroTemplateLogos}>
              {logos?.map((logo) => (
                <SmartImage
                  key={parse(logo.image)}
                  src={parse(logo.image)}
                  alt={parse(logo.alt) ?? 'Logo'}
                  priority
                  height={logo.height}
                  width={logo.width}
                />
              ))}
            </div>
          ) : (
            ''
          )}
        </div>
        {socialProof?.length > 0 ? (
          <div className={styles.heroTemplateSocialProof}>
            <div className={styles.heroTemplateSocialProofContent}>
              {socialProof?.map((item) => (
                <div
                  key={parse(item.name)}
                  className={styles.heroTemplateSocialProofContentItem}
                >
                  <SmartImage
                    src={parse(item.image)}
                    alt={parse(item.name)}
                    priority
                    height={item.height}
                    width={item.width}
                  />
                  <div
                    className={styles.heroTemplateSocialProofContentItemTitle}
                  >
                    {parse(item.name)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          ''
        )}
        {searchBox && (
          <div className={styles.searchBox}>
            <HelpCenterSearch {...searchBox} />
          </div>
        )}
      </div>
    </section>
  );
}

HeroTemplate.defaultProps = {
  title: '',
  headline: '',
  subHeroText: '',
  heroDesc: '',
  promotion: '',
  image: null,
  mobImage: null,
  backgroundSize: 'cover',
  secondaryImage: null,
  mobSecondaryImage: null,
  cta: null,
  stickyButton: null,
  prequal: null,
  theme: null,
  logos: [],
  socialProof: [],
  tiles: null,
  config: {
    gradientMask: null,
  },
  affectCreditScore: null,
  heroWhiteCopyBg: null,
  backgroundPosition: 'center',
  infoCta: null,
  infoIcon: null,
  breadCrumbs: null,
  activePage: null,
  searchBox: null,
};

HeroTemplate.propTypes = {
  title: PropTypes.string,
  headline: PropTypes.string,
  subHeroText: PropTypes.string,
  heroDesc: PropTypes.string,
  promotion: PropTypes.string,
  backgroundSize: PropTypes.string,
  image: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
  }),
  mobImage: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
  }),
  secondaryImage: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
  }),
  mobSecondaryImage: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
  }),
  theme: PropTypes.any,
  prequal: PropTypes.any,
  cta: PropTypes.shape({
    label: PropTypes.string,
    link: PropTypes.string,
  }),
  stickyButton: PropTypes.object,
  infoCta: PropTypes.shape({
    label: PropTypes.string,
    url: PropTypes.string,
  }),
  infoIcon: PropTypes.shape({
    path: PropTypes.string,
    alt: PropTypes.string,
  }),
  logos: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.string,
      alt: PropTypes.string,
    })
  ),
  socialProof: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.string,
      name: PropTypes.string,
    })
  ),
  tiles: PropTypes.string,
  config: PropTypes.any,
  affectCreditScore: PropTypes.string,
  heroWhiteCopyBg: PropTypes.string,
  backgroundPosition: PropTypes.string,
  breadCrumbs: PropTypes.any,
  activePage: PropTypes.string,
  searchBox: PropTypes.object,
};

export default HeroTemplate;
